import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { auditService } from '../../services/auditService';
import { AuditLogEntry } from '../../types/audit';
import UnifiedAuditDiff from './UnifiedAuditDiff';

interface AuditHistoryTabProps {
  resourceType: string;
  resourceId: string;
}

const PAGE_SIZE = 50;

const topChangedKeys = (oldVal: unknown, newVal: unknown, limit = 3): string[] => {
  const toDict = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const a = toDict(oldVal);
  const b = toDict(newVal);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const changed: string[] = [];
  for (const k of keys) {
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) {
      changed.push(k);
      if (changed.length >= limit) break;
    }
  }
  return changed;
};

const AuditHistoryTab: React.FC<AuditHistoryTabProps> = ({ resourceType, resourceId }) => {
  const { t, formatDate, formatRelativeTime } = useI18n();
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showReads, setShowReads] = useState(false);

  const load = useCallback(
    async (targetPage: number, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const response = await auditService.listAuditLog({
          resource_type: resourceType,
          resource_id: resourceId,
          exclude_reads: !showReads,
          page: targetPage,
          page_size: PAGE_SIZE,
        });
        setItems((prev) => (append ? [...prev, ...(response.items || [])] : response.items || []));
        setTotal(response.total || 0);
      } catch (err) {
        console.error('[AuditHistoryTab] load failed:', err);
        setError(err instanceof Error ? err.message : t('audit.history.errorLoading'));
      } finally {
        setLoading(false);
      }
    },
    [resourceType, resourceId, showReads, t],
  );

  useEffect(() => {
    setPage(1);
    setExpanded(null);
    load(1, false);
    // Intentionally omit `load` to avoid the updated callback triggering double loads;
    // the effect re-runs when any of its dependencies (resourceType/resourceId/showReads)
    // would change `load` anyway via load's own deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, resourceId, showReads]);

  const handleLoadMore = useCallback(() => {
    const next = page + 1;
    setPage(next);
    load(next, true);
  }, [page, load]);

  const hasMore = useMemo(() => items.length < total, [items, total]);

  return (
    <div className="audit-history-tab" data-testid="audit-history-tab">
      <div className="audit-history-toolbar">
        <label>
          <input
            type="checkbox"
            checked={showReads}
            onChange={(e) => setShowReads(e.target.checked)}
            data-testid="audit-history-show-reads"
          />
          {showReads ? t('audit.history.hideReads') : t('audit.history.showReads')}
        </label>
        <span className="audit-history-when">
          {t('audit.pagination.pageOf', {
            page: 1,
            total: Math.max(1, Math.ceil(total / PAGE_SIZE)),
          })}{' '}
          · {total}
        </span>
      </div>

      {error && <div className="audit-error-banner">{error}</div>}

      {loading && items.length === 0 ? (
        <div className="audit-loading">{t('audit.history.loading')}</div>
      ) : items.length === 0 ? (
        <div className="audit-empty">{t('audit.history.empty')}</div>
      ) : (
        <div className="audit-history-timeline">
          {items.map((entry) => {
            const isExpanded = expanded === entry.id;
            const changedKeys = topChangedKeys(entry.old_value, entry.new_value);
            return (
              <div
                key={entry.id}
                className={`audit-history-entry${isExpanded ? ' expanded' : ''}`}
                data-testid={`audit-history-entry-${entry.id}`}
              >
                <div
                  className="audit-history-entry-header"
                  onClick={() => setExpanded(isExpanded ? null : entry.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setExpanded(isExpanded ? null : entry.id);
                    }
                  }}
                >
                  <span
                    className={`audit-status-badge ${entry.status}`}
                    title={entry.status}
                  >
                    {entry.status}
                  </span>
                  <span className="audit-history-action">{entry.action}</span>
                  <span
                    className="audit-history-when"
                    title={formatDate(entry.timestamp)}
                  >
                    {formatRelativeTime(entry.timestamp)}
                  </span>
                  <span className="audit-history-actor">
                    {entry.actor_username || t('audit.table.anonymous')}
                  </span>
                </div>
                {changedKeys.length > 0 && !isExpanded && (
                  <div className="audit-history-summary">
                    {changedKeys.join(', ')}
                    {changedKeys.length >= 3 ? '…' : ''}
                  </div>
                )}
                {isExpanded && (
                  <div className="audit-history-detail">
                    <UnifiedAuditDiff
                      oldValue={entry.old_value}
                      newValue={entry.new_value}
                    />
                    {entry.error_message && (
                      <div className="audit-diff-error">
                        <strong>{t('audit.row.errorMessage')}:</strong>{' '}
                        {entry.error_message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <button
          type="button"
          className="audit-history-load-more"
          onClick={handleLoadMore}
          disabled={loading}
          data-testid="audit-history-load-more"
        >
          {t('audit.history.loadMore')}
        </button>
      )}
    </div>
  );
};

export default AuditHistoryTab;
